import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import { jwtDecode } from 'jwt-decode';
import groups from './groups';
import users from './users';
import rooms from './rooms';
import documents from './documents';
import logs from './logs';
import folders from './folders';
import confidential from './confidential';
import organizations from './organizations';
import agreement from './agreement';
import notifications from './notifications';
import roles from './roles';
import notes from './notes';
import chats from './chats';
import share from './share';
import signing from './signing';
import tariffs from './tariffs';
import terms from './terms';
import versions from './versions';
import invites from './invites';
import editing from './document_editing';
import aiAssistant from './ai_assistant';
import searcher from './searcher';
import ocr from './ocr';
import externalAuth from './external_auth';

import blobErrorToObject from 'helpers/blobErrorToObject';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const requestTemplate = axios.create({
  baseURL: window.REACT_APP_SERVER_API !== 'REPLACE_REACT_APP_SERVER_API' ? window.REACT_APP_SERVER_API : process.env.REACT_APP_SERVER_API || 'http://localhost:8000/api',
  // withCredentials: true,
  headers: correctHeaders,
})

requestTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();
  
    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };
  
    config.headers!.Authorization = `Bearer ${accessToken}`;
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

requestTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {  
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 1) {
    cntTryingUpdateFingerprint++;
    
    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');
      
      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return requestTemplate.request(error.request.responseURL);
    } catch (e) {
      console.log(error, 'error while updating fingerprint');
      resetTokensAndSendToLogin();
    }
  } else if (error.response?.data?.detail === 'Invalid access token' || cntTryingUpdateFingerprint >= 1) {
    resetTokensAndSendToLogin();
  }
  
  if (error?.response?.data.constructor.name === 'Blob'){
    error.response.data = await blobErrorToObject(error?.response?.data)
  };
  return Promise.reject(error)
});

const api = {
  ...groups,
  ...users,
  ...rooms,
  ...documents,
  ...logs,
  ...folders,
  ...confidential,
  ...organizations,
  ...agreement,
  ...notifications,
  ...notes,
  ...chats,
  ...roles,
  ...share,
  ...signing,
  ...terms,
  ...tariffs,
  ...versions,
  ...invites,
  ...editing,
  ...aiAssistant,
  ...searcher,
  ...ocr,
  ...externalAuth,
}

export { requestTemplate };
export default api
